import Swiper from "./swiper-bundle.esm.browser.min.js";
import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

const badges = document.querySelector(".photoswipe > .card__badges");

const lightbox = new PhotoSwipeLightbox({
  // may select multiple "galleries"
  gallery: ".photoswipe",

  // Elements within gallery (slides)
  children: "a",

  // setup PhotoSwipe Core dynamic import
  pswpModule: () => import("./photoswipe.esm.min.js"),

  bgOpacity: 1,
});
lightbox.init();
lightbox.on("beforeOpen", () => {
  badges?.classList.add("hide");
});

lightbox.on("destroy", () => {
  badges?.classList.remove("hide");
});

/**
 * `lightbox.init()` only binds to `.photoswipe` elements present at load.
 * Quick view injects the gallery after fetch; bind the same click handler
 * to new instances only (idempotent per element).
 */
function wtBindPhotoSwipeGalleriesIn(root) {
  if (!lightbox || !root) return;
  const nodes = root.matches?.(".photoswipe")
    ? [root]
    : root.querySelectorAll?.(".photoswipe");
  if (!nodes || !nodes.length) return;
  const handler = lightbox.onThumbnailsClick?.bind(lightbox);
  if (!handler) return;
  nodes.forEach((galleryEl) => {
    if (galleryEl.dataset.wtPswpClickBound) return;
    galleryEl.dataset.wtPswpClickBound = "1";
    galleryEl.addEventListener("click", handler, false);
  });
}
if (typeof window !== "undefined") {
  window.wtBindPhotoSwipeGalleriesIn = wtBindPhotoSwipeGalleriesIn;
}

const WT_GALLERY_UNMUTE_CLASS = "wt-hero-video__sound-toggle--unmuted";

/**
 * The gallery filters/clones/re-attaches slides several times during boot
 * (initial render -> alt-filter -> Swiper init -> variant change). Every
 * time a <video><source src="…mp4"></video> is attached to the DOM, the
 * browser starts a fresh range request — even on slides that will never
 * play. With 3-4 attach cycles per render that's 5-6 redundant HD-1080p
 * fetches per video, which hammers initial page load.
 *
 * The two helpers below pair with stripGalleryVideoSources() (called once
 * in connectedCallback): on boot every <source src> is moved to
 * data-lpv-src, and is only restored when a slide actually becomes the
 * active video to play. Clones inherit the stripped state, so attach
 * cycles cost nothing.
 */
function restoreGalleryVideoSources(video) {
  if (!video || video.tagName !== "VIDEO") return false;
  const sources = video.querySelectorAll("source[data-lpv-src]");
  if (sources.length === 0) return false;
  sources.forEach((source) => {
    source.setAttribute("src", source.dataset.lpvSrc);
    delete source.dataset.lpvSrc;
  });
  try {
    video.load();
  } catch (_) {}
  return true;
}

function stripGalleryVideoSources(container) {
  if (!container) return;
  const videos = container.querySelectorAll("video");
  videos.forEach((video) => {
    const sources = video.querySelectorAll("source[src]");
    if (sources.length === 0) return;
    sources.forEach((source) => {
      source.dataset.lpvSrc = source.getAttribute("src");
      source.removeAttribute("src");
    });
    try {
      video.load();
    } catch (_) {}
  });
}

/**
 * Product media often lists HLS (.m3u8) before MP4; play() may reject until data loads.
 * Collage/masonry layouts never run Swiper afterInit, so HTML autoplay alone is unreliable.
 */
function tryPlayProductVideo(video) {
  if (!video || video.tagName !== "VIDEO") return;
  video.muted = true;
  restoreGalleryVideoSources(video);
  const attempt = () => {
    void video.play().catch(() => {});
  };
  attempt();
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    video.addEventListener("loadeddata", attempt, { once: true });
    video.addEventListener("canplay", attempt, { once: true });
  }
}

function handleGallerySlideVideos(swiper) {
  if (!swiper?.slides?.length) return;
  const allVideos = Array.from(swiper.slides).flatMap((slide) =>
    Array.from(slide.querySelectorAll("video")),
  );
  const allToggleBtns = Array.from(swiper.slides).flatMap((slide) =>
    Array.from(slide.querySelectorAll(".wt-hero-video__sound-toggle")),
  );

  allVideos.forEach((video) => {
    video.pause();
    video.currentTime = 0;
    video.muted = true;
    video.controls = false;
    clearTimeout(video._hideCtrlsTimer);
  });

  allToggleBtns.forEach((btn) => {
    btn.setAttribute("data-sound", "off");
    btn.classList.remove(WT_GALLERY_UNMUTE_CLASS);
  });

  const activeSlide = swiper.slides[swiper.activeIndex];
  const activeVideo = activeSlide?.querySelector("video");
  if (activeVideo) tryPlayProductVideo(activeVideo);
}

function initStaticGalleryProductVideos(galleryEl) {
  galleryEl?.querySelectorAll?.("video").forEach(tryPlayProductVideo);
}

function initGalleryVideoInteractions(swiper) {
  handleGallerySlideVideos(swiper);
  if (swiper.el.dataset.wtGalleryVideoClickBound === "true") return;
  swiper.el.dataset.wtGalleryVideoClickBound = "true";
  swiper.el.addEventListener("click", (e) => {
    const video = e.target.closest("video");
    if (video) {
      video.controls = true;
      video.blur();
      clearTimeout(video._hideCtrlsTimer);
      video._hideCtrlsTimer = setTimeout(() => {
        video.controls = false;
      }, 3000);
    }
  });
}

/**
 * Opt-in: ?wt_debug_gallery=1 in URL, or in console: window.wtDebugGallery = true; location.reload();
 */
function wtGalleryDebug(label, detail) {
  if (typeof window === "undefined") return;
  if (
    !window.wtDebugGallery &&
    !/(?:\?|&)wt_debug_gallery=1(?:&|$)/.test(window.location?.search || "")
  ) {
    return;
  }
  if (detail !== undefined) {
    console.log("[gallery-section]", label, detail);
  } else {
    console.log("[gallery-section]", label);
  }
}
if (typeof window !== "undefined") {
  window.wtGalleryDebug = wtGalleryDebug;
}

/*
  Reserved alt-text flag that, when present on a product media's alt
  attribute, elevates that media to the lead position of the PDP gallery
  (and behind it, the variant's `featured_media`).

  Lead promotion deliberately does NOT re-implement scoping — it rides
  on top of the existing variant-alt-filter (slideMatchesAltFilterMode
  + filterSlidesByOptionsLegacy), which already understands every
  scoping convention this theme supports. Anything the filter keeps for
  the current variant is, by definition, in-scope for that variant, so
  among the survivors we just pick the first slide tagged `#lead`.
  That single rule makes all three of the supported scoping styles
  work consistently:

    alt = "Hero shot #blue #lead"       → hashtag-scoped: lead when blue is selected
    alt = "Hero shot #blue|red #lead"   → hashtag OR: lead when blue OR red is selected
    alt = "sand #lead"                  → plain-token-scoped (legacy): lead when sand is selected
    alt = "Hero shot #all #lead"        → explicit global: lead for every variant
    alt = "#lead"                       → implicit global: lead for every variant

    alt = "Detail shot #blue"           → regular blue-scoped gallery image (no lead promotion)

  Conflict / edge-case behavior:
   - Multiple `#lead` slides surviving the filter for the same variant
     (intentional or accidental duplicates): first in product-media
     order wins. Reorder media in Shopify Admin to control which one.
   - `#lead` with scoping that doesn't match any current option (e.g.
     "Lifestyle #lead" where "Lifestyle" isn't an option value): the
     existing filter drops the slide for that variant, so it's neither
     visible NOR a lead. To make a slide a true global lead, drop all
     scoping ("#lead" alone) or use the explicit "#all #lead".
   - Filter returns empty for the current variant (merchant forgot to
     scope media for this option): the empty-result fallback restores
     the unfiltered master set as-is and the lead hoist is skipped, so
     we don't accidentally elevate a slide tagged for a different
     variant.

  Opt-in: rides on the existing variant-alt-filter opt-in (see
  settings.variant_media_alt_enable_tag /
  settings.variant_media_alt_enable_metafield). Products that don't
  enable variant-alt-filter won't see lead promotion even if `#lead`
  appears in their alt text — gated centrally in
  findLeadMediaIdAmongFiltered so init and variant-change paths
  behave identically.

  See:
   - assets/gallery.js → slideMatchesAltFilterMode / filterSlidesByOptionsLegacy
     (exempts `#lead` from filter membership so a lead-tagged slide
     isn't accidentally filtered out for "lead" failing to match any
     option value)
   - sortSlides / findLeadMediaIdAmongFiltered (hoists lead to position
     0; variant-featured is now spliced out of the PDP gallery when a
     #lead exists, see sortSlides comment)

  `#collectionlead` is a sibling directive for collection cards (the
  4:5 hero shot merchants pick for swatches and accessory pins). PDP
  gallery never renders these — Liquid drops them in main-product.liquid
  before they hit the DOM. The token is still exempted from filter math
  here as belt-and-suspenders insurance against future render paths
  that might forget to strip it.
*/
const VARIANT_GALLERY_LEAD_TOKEN = "lead";
const VARIANT_GALLERY_COLLECTION_LEAD_TOKEN = "collectionlead";
const VIDEO_POSTER_COVER_CLASS = "wt-video-poster-cover";

/**
 * Media element used for variant alt filtering and data-media-id lookup.
 * Skips the decorative iOS poster cover (assets/custom.js) which has alt=""
 * and would otherwise make every video slide visible for all variants.
 */
function getSlideFilterMedia(slide) {
  if (!slide) return null;

  const video = slide.querySelector("video[data-media-id]");
  if (video) return video;

  const img = slide.querySelector(
    `img[data-media-id]:not(.${VIDEO_POSTER_COVER_CLASS})`,
  );
  if (img) return img;

  return (
    slide.querySelector("video") ||
    slide.querySelector(`img:not(.${VIDEO_POSTER_COVER_CLASS})`)
  );
}

if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      updateSwiperAutoHeight = () => {
        if (this.resolution != this.matchResolution()) {
          this.resolution = this.matchResolution();
          this.destroyCarouselGallery();
          this.initializeGallery();
          this.setStickyGallery();
        }
      };

      constructor() {
        super();
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";

        this.galleryLoader = this.querySelector("#gallery-loader");
        this.renderedSlides = [];

        // Strip <source src> from every product-media <video> before
        // readConfiguration() takes its master clones. This both aborts
        // any range-request the HTML parser already kicked off (via
        // video.load()) and ensures every subsequent cloneNode/attach
        // cycle reuses the stripped state, so only the slide that
        // actually becomes active will ever fetch its mp4.
        stripGalleryVideoSources(this);

        try {
          this.readConfiguration();
        } catch (err) {
          console.error("gallery-section: readConfiguration", err);
          this.classList.remove("loading");
          this.classList.add("loaded");
          return;
        }

        const bootGallery = () => {
          /* Never skip `finally`: an early return when !isConnected would leave .loading forever. */
          try {
            if (!this.isConnected) {
              wtGalleryDebug("boot: skipped, disconnected");
              return;
            }
            if (this.isVariantAltFilterEnabled()) {
              this.applyInitialAltFilterBeforeInit();
            }
            this.initializeGallery();
            if (this._isInQuickBuyDrawer()) {
              console.info(
                "[gallery-section] quick view · layout=%s · resolution=%s · main Swiper: %s (verbose: add ?wt_debug_gallery=1 to URL or window.wtDebugGallery = true)",
                this.configuration?.desktopLayout ?? "?",
                this.matchResolution() ?? "?",
                this.gallerySwiper ? "yes" : "no",
              );
            }
            wtGalleryDebug("boot: after initializeGallery", {
              desktopLayout: this.configuration?.desktopLayout,
              matchResolution: this.matchResolution(),
              inQuickBuy: this._isInQuickBuyDrawer(),
              gallerySwiper: Boolean(this.gallerySwiper),
            });
            this.galleryLoader?.classList.add("hidden");
            this.setStickyGallery();
          } catch (err) {
            console.error("gallery-section: init", err);
          } finally {
            this.classList.remove("loading");
            this.classList.add("loaded");
            if (this._isInQuickBuyDrawer()) {
              this._scheduleQuickBuySwiperRetryIfNeeded();
            }
          }
        };

        const runAfterLayout = (fn) => {
          if (this._isInQuickBuyDrawer()) {
            /* Drawer can be 0px wide for a frame; Swiper 8 can fail; retry also covers late layout. */
            requestAnimationFrame(() => requestAnimationFrame(fn));
          } else {
            fn();
          }
        };

        if (this.isVariantAltFilterEnabled()) {
          queueMicrotask(() => runAfterLayout(bootGallery));
        } else {
          runAfterLayout(bootGallery);
        }
      }

      _isInQuickBuyDrawer() {
        return Boolean(
          this.closest(".wt__quick-buy") ||
            this.closest(".wt__quick-buy--wrapper") ||
            this.closest(".wt__quick-buy__container"),
        );
      }

      /**
       * First init can run with 0-width drawer; second pass after paint fixes Swiper.
       */
      _scheduleQuickBuySwiperRetryIfNeeded() {
        if (this._wtQuickBuyGalleryRetryScheduled) {
          return;
        }
        if (!this._isInQuickBuyDrawer()) {
          return;
        }
        this._wtQuickBuyGalleryRetryScheduled = true;
        const run = () => {
          if (!this.isConnected) {
            return;
          }
          if (this.gallerySwiper) {
            try {
              this.gallerySwiper.update();
              this.thumbsSwiper?.update?.();
            } catch (e) {
              console.warn("gallery-section: quick-buy Swiper.update", e);
            }
            wtGalleryDebug("quick-buy retry: had Swiper, update()");
            return;
          }
          wtGalleryDebug("quick-buy retry: no gallerySwiper, re-initializing", {
            w: this.elements?.gallery?.clientWidth,
            layout: this.configuration?.desktopLayout,
          });
          try {
            this.destroyCarouselGallery();
            this.destroyCollageGallery();
            this.destroyMasonryGallery();
            this.initializeGallery();
            if (this.gallerySwiper) {
              this.galleryLoader?.classList.add("hidden");
            }
            wtGalleryDebug("quick-buy retry: done", {
              gallerySwiper: Boolean(this.gallerySwiper),
            });
            if (this.gallerySwiper) {
              console.info(
                "[gallery-section] quick view (retry) · main Swiper: yes",
              );
            } else {
              console.warn(
                "[gallery-section] quick view: main Swiper still missing after retry — see gallery-section: init errors above",
              );
            }
          } catch (e) {
            console.error("gallery-section: quick-buy retry", e);
          }
        };
        setTimeout(run, 150);
      }

      disconnectedCallback() {
        // this.destroyGallery();
      }

      setStickyGallery() {
        if (this.isProductPage) {
          const headerHeight =
            document.querySelector(".wt-header")?.offsetHeight;
          const swiperRect = this.getBoundingClientRect();

          const fitsInViewport =
            swiperRect.top >= 0 &&
            swiperRect.left >= 0 &&
            swiperRect.height <= window.innerHeight;

          const positionTopValue = `${headerHeight + 16}px`;

          this.style.setProperty("--position-top", positionTopValue);
          this.classList.add("wt-product__gallery--sticky", fitsInViewport);
        }
      }

      readConfiguration() {
        // general configuration
        this.configuration = [];

        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]").querySelector(
            "[data-swiper-container]",
          ),
          thumbsContainer: this.querySelector("[data-thumbs]").querySelector(
            "[data-swiper-container]",
          ),
        };

        this._masterGallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this._masterThumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this.elements.gallerySlides = this._masterGallerySlides;
        this.elements.thumbsSlides = this._masterThumbsSlides;

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
        };

        const custom_configuration = JSON.parse(
          this.elements.section.querySelector("[data-configuration]").innerHTML,
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        let autoHeightEnabled = window.innerWidth <= 768;

        // gallery swiper configuration
        const default_gallery_configuration = {
          autoHeight: autoHeightEnabled,
          threshold: 10,
          grabCursor: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: {
            el: ".wt-slider__scrollbar",
            draggable: true,
          },
          pagination: {
            el: ".swiper-pagination",
            type: "fraction",
          },
          on: {
            afterInit: initGalleryVideoInteractions,
            slideChangeTransitionEnd: handleGallerySlideVideos,
          },
        };

        const custom_gallery_configuration = JSON.parse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const updateThumbsSwiperClasses = (swiperInstance) => {
          const container = swiperInstance.el;
          const cls = {
            beginning: "swiper-at-beginning",
            end: "swiper-at-end",
            locked: "swiper-locked",
            ready: "swiper-thumbs-ready",
          };

          container.classList.remove(
            cls.beginning,
            cls.end,
            cls.locked,
            cls.ready,
          );

          if (swiperInstance.isLocked) {
            container.classList.add(cls.locked);
            return;
          }

          container.classList.add(cls.ready);

          if (swiperInstance.isBeginning)
            container.classList.add(cls.beginning);
          if (swiperInstance.isEnd) container.classList.add(cls.end);
        };

        // gallery thumbs swiper configuration
        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 4,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          on: {
            afterInit(swiper) {
              updateThumbsSwiperClasses(swiper);
            },
            lock: updateThumbsSwiperClasses,
            unlock: updateThumbsSwiperClasses,
            slideChange: updateThumbsSwiperClasses,
            observerUpdate: updateThumbsSwiperClasses,
            resize: updateThumbsSwiperClasses,
          },
        };

        const custom_thumbs_configuration = JSON.parse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
        this.bindScopedSwiperElReferences();
        this.applyQuickViewGalleryConfiguration();
        this.resolution = this.matchResolution();
      }

      /**
       * Quick view: no thumb strip, wider effective viewport, but window is still
       * "desktop" — strip breakpoints and use a fractional slidesPerView so the
       * next slide peeks in the narrow drawer.
       */
      applyQuickViewGalleryConfiguration() {
        if (!this._isInQuickBuyDrawer()) return;
        const g = { ...this.gallery_configuration };
        delete g.breakpoints;
        delete g.thumbs;
        this.gallery_configuration = {
          ...g,
          slidesPerView: 1.3,
          spaceBetween: 8,
          watchOverflow: false,
        };
        wtGalleryDebug("quick view gallery config (peek)", {
          slidesPerView: this.gallery_configuration.slidesPerView,
        });
      }

      /**
       * Swiper’s string selectors (e.g. .wt-slider__nav-next) are resolved globally.
       * Each gallery + thumbs has its own pair; the wrong target breaks init in quick view.
       */
      bindScopedSwiperElReferences() {
        const g = this.elements?.gallery;
        const t = this.elements?.thumbs;
        if (!g) return;

        const gNext = g.querySelector(".wt-slider__nav-next, .swiper-button-next");
        const gPrev = g.querySelector(".wt-slider__nav-prev, .swiper-button-prev");
        if (gNext && gPrev) {
          this.gallery_configuration = {
            ...this.gallery_configuration,
            navigation: {
              ...this.gallery_configuration.navigation,
              nextEl: gNext,
              prevEl: gPrev,
            },
          };
        }

        if (t) {
          const tNext = t.querySelector(
            ".wt-slider__nav-next, .swiper-button-next",
          );
          const tPrev = t.querySelector(
            ".wt-slider__nav-prev, .swiper-button-prev",
          );
          if (tNext && tPrev) {
            this.thumbs_configuration = {
              ...this.thumbs_configuration,
              navigation: {
                ...this.thumbs_configuration.navigation,
                nextEl: tNext,
                prevEl: tPrev,
              },
            };
          }
        }

        if (this.gallery_configuration?.scrollbar) {
          const elHint = this.gallery_configuration.scrollbar.el;
          if (typeof elHint === "string") {
            const el = g.querySelector(elHint);
            if (el) {
              this.gallery_configuration = {
                ...this.gallery_configuration,
                scrollbar: { ...this.gallery_configuration.scrollbar, el },
              };
            }
          } else if (!elHint) {
            const el = g.querySelector(
              ".wt-slider__scrollbar--featured, .wt-slider__scrollbar",
            );
            if (el) {
              this.gallery_configuration = {
                ...this.gallery_configuration,
                scrollbar: { ...this.gallery_configuration.scrollbar, el },
              };
            }
          }
        }
      }

      initializeGallery() {
        window.addEventListener("resize", () => {
          this.updateSwiperAutoHeight(this.gallerySwiper);
        });

        switch (this.configuration.desktopLayout) {
          case "carousel-vertical":
          case "carousel-horizontal":
            this.initializeCarouselGallery();
            break;
          case "fashion":
            /* Theme markup uses the same structure as masonry (swiper-wrapper--masonry). */
          case "masonry":
            window.addEventListener(
              "resize",
              function () {
                this.handleMassonry();
              }.bind(this),
            );
            this.handleMassonry();
            break;
          case "collage":
            window.addEventListener(
              "resize",
              function () {
                this.handleCollage();
              }.bind(this),
            );
            this.handleCollage();
            break;
        }
      }

      initializeCarouselGallery() {
        if (!this._isInQuickBuyDrawer()) {
          if (this.thumbsSwiper == null) {
            this.decorateSwiper(
              this.elements.thumbs,
              "wt-slider__container--thumbs",
            );
            this.thumbsSwiper = this.swiperThumbsInitilize();
          }
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize(
          this._isInQuickBuyDrawer() ? null : this.thumbsSwiper,
        );

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide) {
          disableTouchSlide.classList.add("swiper-no-swiping");
        }
      }

      swiperGalleryInitilize(thumbsSwiper) {
        if (this.gallerySwiper == null) {
          const thumbs_configuration = {
            thumbs: {
              swiper: thumbsSwiper,
            },
          };
          const desktopRatio = this.getAttribute("desktop-ratio");

          const useAlwaysAutoHeight =
            (this.configuration.desktopLayout === "carousel-vertical" &&
              desktopRatio === "original") ||
            this.configuration.desktopLayout === "carousel-horizontal";

          let autoHeightEnabled = useAlwaysAutoHeight
            ? true
            : window.innerWidth <= 900;
          if (thumbsSwiper) {
            this.gallery_configuration = {
              ...this.gallery_configuration,
              ...thumbs_configuration,
              autoHeight: autoHeightEnabled,
            };
          } else {
            this.gallery_configuration = {
              ...this.gallery_configuration,
              autoHeight: autoHeightEnabled,
            };
          }
          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.gallery_configuration,
          );
        }
      }

      swiperThumbsInitilize() {
        let autoHeightEnabled = window.innerWidth <= 900;
        this.thumbs_configuration = {
          ...this.thumbs_configuration,
          autoHeight: autoHeightEnabled,
        };
        const swiper = new Swiper(
          this.elements.thumbs,
          this.thumbs_configuration,
        );
        return swiper;
      }

      destroyCarouselGallery() {
        if (this.thumbsSwiper != null) {
          this.thumbsSwiper.destroy();
          this.thumbsSwiper = null;
          this.undecorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
        }
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      handleMassonry() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeMasonryGallery();
        } else {
          this.destroyMasonryGallery();
          this.initializeCarouselGallery();
        }
      }

      handleCollage() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeCollageGallery();
        } else {
          this.destroyCollageGallery();
          this.initializeCarouselGallery();
        }
      }

      decorateSwiper(el, element_class) {
        el.classList.add("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]")?.classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.galleryUpdateEvent();
      }

      undecorateSwiper(el, element_class) {
        el.classList.remove("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
      }

      decorateCollage(el, element_class) {
        el.classList.add(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.add(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add(`wt-${element_class}__slide`);
        });
        this.galleryUpdateEvent();
      }

      undecorateCollage(el, element_class) {
        el.classList.remove(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.remove(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove(`wt-${element_class}__slide`);
        });
      }

      initializeCollageGallery() {
        this.decorateCollage(this.elements.gallery, "collage");
        initStaticGalleryProductVideos(this.elements.gallery);
      }

      destroyCollageGallery() {
        this.undecorateCollage(this.elements.gallery, "collage");
      }

      initializeMasonryGallery() {
        this.decorateCollage(this.elements.gallery, "masonry");
        initStaticGalleryProductVideos(this.elements.gallery);
      }

      destroyMasonryGallery() {
        this.undecorateCollage(this.elements.gallery, "masonry");
      }

      /*
        Find the first slide in `filteredSlides` that's flagged as the
        lead via `#lead`. Returns the media id (string) or null.

        Scoping is intentionally NOT re-evaluated here — `filteredSlides`
        is the post-filter list, so the variant-scoping rules
        (slideMatchesAltFilterMode) have already decided which slides
        belong to the current variant. That filter understands all
        three conventions in one place:

          - Hashtag scoping        alt = "Hero #blue #lead"
          - Plain-token scoping    alt = "sand #lead"           (legacy)
          - Global / #all          alt = "Hero #all #lead", "#lead"

        So whatever survives the filter is, by definition, in-scope for
        this variant; the lead detector only has to ask "does any
        survivor carry #lead?" and pick the first one. Two important
        consequences worth calling out for merchants debugging alt text:

          1. `"sand #lead"` is the sand variant's lead, NOT a global
             lead — the plain "sand" token scopes the slide via the
             filter, the filter drops it for every other variant, and
             only sand-selected sessions ever see this slide as a lead
             candidate.

          2. `"#lead"` alone IS a global lead — no scoping makes the
             filter keep it for every variant, so it surfaces as the
             lead candidate in every variant's filter result.

        Tie-breaker for multiple #lead candidates surviving the filter
        (e.g., merchant accidentally tagged two slides with the same
        variant): first in product-media order wins, which is the
        order Shopify Admin uses for product media — predictable, and
        merchant-controllable by dragging media in Admin.

        Lead promotion is gated on the same opt-in as the rest of the
        variant-alt-filter system (`data-variant-alt-filter="true"`,
        set by main-product.liquid when the configured tag or metafield
        is present on the product). Otherwise init and variant-change
        paths would diverge — applyInitialAltFilterBeforeInit returns
        early when the feature is disabled, but filterSlides (variant
        change) does not, so a lead could silently appear after the
        first option swap on products the merchant hasn't opted in.
      */
      findLeadMediaIdAmongFiltered(filteredSlides) {
        if (!this.isVariantAltFilterEnabled()) return null;
        for (const slide of filteredSlides) {
          const media = getSlideFilterMedia(slide);
          if (!media) continue;
          const alt = media.getAttribute("alt") || "";
          const hashtagMatches = alt.match(/#[^\s#]+/g) || [];
          const hasLead = hashtagMatches.some(
            (h) => h.slice(1).toLowerCase() === VARIANT_GALLERY_LEAD_TOKEN,
          );
          if (hasLead) {
            return media.getAttribute("data-media-id");
          }
        }
        return null;
      }

      /*
        sortSlides hoists the variant's featured_media to position 0 so
        the gallery opens on that image when a variant is selected. When
        a #lead is designated for the current variant, the lead takes
        position 0 instead and the variant featured_media is REMOVED
        from the PDP gallery entirely (it's typically the 1:1 thumbnail
        Shopify uses for cart/checkout/upsell — keeping it in the
        non-square PDP gallery defeats the purpose of letting the
        merchant designate a #lead in the first place). Cart, upsell,
        and other surfaces still get the variant featured_media because
        they iterate product.media independently. If lead === featured
        (the same media carries both attributes), the splice-then-hoist
        collapses to a single hoist — that's an intentional merchant
        opt-out: tag the variant featured_media with #lead to keep it
        in the gallery.
      */
      sortSlides(slides, featured_media_id, lead_media_id = null) {
        const findIndexByMediaId = (mediaId) =>
          slides.findIndex((slide) => {
            const el = getSlideFilterMedia(slide);
            if (!el) return false;
            const raw = el.getAttribute("data-media-id");
            return raw != null && Number(raw) === Number(mediaId);
          });

        if (lead_media_id && Number(lead_media_id) !== Number(featured_media_id)) {
          /*
            Lead exists and is distinct from featured. Splice out the
            variant featured_media so it disappears from the PDP gallery,
            then hoist the lead to position 0. Order matters: removing
            featured first means leadIndex still points to the right
            slide (no array-shift caveats from splicing after lookup).
          */
          const featuredIndex = findIndexByMediaId(featured_media_id);
          if (featuredIndex > -1) {
            slides.splice(featuredIndex, 1);
          }
          const leadIndex = findIndexByMediaId(lead_media_id);
          if (leadIndex > -1) {
            const [leadSlide] = slides.splice(leadIndex, 1);
            slides.unshift(leadSlide);
          }
          return slides;
        }

        // Find the index of the slide with featured_media_id
        const effectiveId = lead_media_id || featured_media_id;
        const featuredIndex = findIndexByMediaId(effectiveId);

        // If a slide with featured_media_id is found, move it to the beginning of the array
        if (featuredIndex > -1) {
          const featuredSlide = slides[featuredIndex];
          slides.splice(featuredIndex, 1);
          slides.unshift(featuredSlide);
        }

        return slides;
      }

      beforeGalleryChange() {
        this.classList.add("loading");
        this.galleryLoader?.classList.remove("hidden");
        this.style.minHeight = `${this.offsetHeight}px`;
      }

      afterGalleryChange() {
        this.style.minHeight = "unset";
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        if (
          this.elements?.gallery &&
          (this.configuration?.desktopLayout === "collage" ||
            this.configuration?.desktopLayout === "masonry" ||
            this.configuration?.desktopLayout === "fashion") &&
          this.matchResolution() === "desktop"
        ) {
          initStaticGalleryProductVideos(this.elements.gallery);
        }
      }

      isVariantAltFilterEnabled() {
        return this.dataset.variantAltFilter === "true";
      }

      plainTokenMatchModeIsAll() {
        return this.dataset.variantAltMatchMode === "all";
      }

      normalizeOptionValue(option) {
        return String(option ?? "")
          .toLowerCase()
          .replace(/\s/g, "");
      }

      getSelectedVariantOptionsFromDom(section) {
        const variantOptions = section.querySelector("variant-options");
        if (!variantOptions) return [];
        const fieldsets = Array.from(
          variantOptions.querySelectorAll(".wt-product__option"),
        );
        return fieldsets.map((fieldset) => {
          const input = Array.from(fieldset.querySelectorAll("input")).find(
            (radio) => radio.checked,
          );
          return input?.value || "";
        });
      }

      getInitialOptionsFromDataset() {
        const namesRaw = this.dataset.initialOptionNames;
        const valuesRaw = this.dataset.initialOptionValues;
        if (!namesRaw || !valuesRaw) return null;
        try {
          const names = JSON.parse(namesRaw);
          const values = JSON.parse(valuesRaw);
          if (!Array.isArray(names) || !Array.isArray(values)) return null;
          if (names.length !== values.length) return null;
          return values.map((v) => (v == null ? "" : String(v)));
        } catch {
          return null;
        }
      }

      resolveOptionsForAltMatch(rawOptions, section) {
        const name = (this.dataset.variantMediaOptionName || "").trim();
        if (!name) return rawOptions;

        const needle = name.toLowerCase();
        const variantOptions = section?.querySelector("variant-options");
        if (variantOptions) {
          const fieldsets = Array.from(
            variantOptions.querySelectorAll(".wt-product__option"),
          );
          for (let i = 0; i < fieldsets.length; i++) {
            const labelEl = fieldsets[i].querySelector(
              ".wt-product__option__title .label",
            );
            const labelText = (labelEl?.textContent || "")
              .replace(/\s*:\s*$/u, "")
              .trim()
              .toLowerCase();
            if (
              labelText === needle &&
              rawOptions[i] !== undefined &&
              rawOptions[i] !== ""
            ) {
              return [rawOptions[i]];
            }
          }
        }

        try {
          const names = JSON.parse(this.dataset.initialOptionNames || "[]");
          if (Array.isArray(names)) {
            const idx = names.findIndex(
              (n) => String(n).trim().toLowerCase() === needle,
            );
            if (
              idx >= 0 &&
              rawOptions[idx] !== undefined &&
              String(rawOptions[idx]).trim()
            ) {
              return [rawOptions[idx]];
            }
          }
        } catch {
          /* ignore */
        }

        return rawOptions;
      }

      applyInitialAltFilterBeforeInit() {
        if (!this.isVariantAltFilterEnabled()) return;
        if (!this._masterGallerySlides?.length) return;
        const section = this.closest("section[data-product-handle]");
        if (!section) return;

        let rawOptions = this.getSelectedVariantOptionsFromDom(section);
        if (
          !rawOptions.length ||
          rawOptions.every((o) => !String(o).trim())
        ) {
          const fromDataset = this.getInitialOptionsFromDataset();
          if (fromDataset?.length) rawOptions = fromDataset;
        }
        const optionsForMatch = this.resolveOptionsForAltMatch(
          rawOptions,
          section,
        );
        const featuredId =
          this.dataset.initialFeaturedMediaId &&
          this.dataset.initialFeaturedMediaId !== ""
            ? this.dataset.initialFeaturedMediaId
            : false;

        let filteredGallerySlides = this.filterSlidesByOptions(
          this._masterGallerySlides.map((el) => el.cloneNode(true)),
          optionsForMatch,
          featuredId,
          true,
        );
        let filteredThumbsSlides = this.filterSlidesByOptions(
          this._masterThumbsSlides.map((el) => el.cloneNode(true)),
          optionsForMatch,
          featuredId,
          true,
        );

        /*
          Track whether the empty-result fallback kicks in so we know
          not to lead-promote off the unfiltered master set below. If
          the filter returned nothing for the current variant (merchant
          forgot to scope media for this option, or an over-strict
          matchAll setting), showing the master set as-is is the
          existing recovery behavior — re-introducing lead promotion
          off media tagged for OTHER variants would silently surface
          the wrong hero.
        */
        const initialFilterReturnedEmpty = filteredGallerySlides.length === 0;
        if (initialFilterReturnedEmpty) {
          filteredGallerySlides = this._masterGallerySlides.map((el) =>
            el.cloneNode(true),
          );
          filteredThumbsSlides = this._masterThumbsSlides.map((el) =>
            el.cloneNode(true),
          );
        }

        /*
          `findLeadMediaIdAmongFiltered` returns the first surviving
          slide tagged `#lead`. We pass it straight to sortSlides
          which places it at position 0 and shuffles the variant
          featured_media to position 1; when no `#lead` is found
          sortSlides falls back to today's "featured first" behavior
          with zero observable change.
        */
        const initialLeadMediaId = initialFilterReturnedEmpty
          ? null
          : this.findLeadMediaIdAmongFiltered(filteredGallerySlides);

        if (featuredId || initialLeadMediaId) {
          filteredGallerySlides = this.sortSlides(
            filteredGallerySlides,
            featuredId,
            initialLeadMediaId,
          );
          filteredThumbsSlides = this.sortSlides(
            filteredThumbsSlides,
            featuredId,
            initialLeadMediaId,
          );
        }

        const galleryWrapper = this.elements.galleryContainer;
        const thumbsWrapper = this.elements.thumbsContainer;
        if (!galleryWrapper || !thumbsWrapper) return;

        galleryWrapper.replaceChildren(
          ...filteredGallerySlides.map((slide) => slide.cloneNode(true)),
        );
        thumbsWrapper.replaceChildren(
          ...filteredThumbsSlides.map((slide) => slide.cloneNode(true)),
        );

        this.renderedSlides = filteredGallerySlides.map((el) =>
          el.cloneNode(true),
        );
      }

      filterSlidesByOptionsLegacy(
        slides,
        options,
        featured_media_id,
        matchAll = true,
      ) {
        const lowercaseOptions = options.map((option) =>
          this.normalizeOptionValue(option),
        );

        return slides.filter((slide) => {
          const media = getSlideFilterMedia(slide);
          const alt = media ? media.getAttribute("alt") : "";
          const mediaId = media ? media.getAttribute("data-media-id") : "";

          if (
            featured_media_id &&
            String(mediaId) === String(featured_media_id)
          ) {
            return true;
          }

          /*
            Same reserved-flag exemption as slideMatchesAltFilterMode:
            #lead and #collectionlead are display directives, not filter
            tokens. Drop them before the membership math so legacy-mode
            products that adopt the lead convention still see their
            lead media included instead of filtered out.
          */
          const altHashtags = (alt?.match(/#[^\s#]+/g) || [])
            .map((hashtag) => hashtag.slice(1).toLowerCase())
            .filter(
              (tag) =>
                tag !== VARIANT_GALLERY_LEAD_TOKEN &&
                tag !== VARIANT_GALLERY_COLLECTION_LEAD_TOKEN,
            );

          if (altHashtags.length === 0) return true;
          if (altHashtags.some((tag) => tag.split("|").includes("all")))
            return true;

          if (matchAll) {
            return altHashtags.every((tag) =>
              tag
                .split("|")
                .some((variant) =>
                  lowercaseOptions.includes(
                    this.normalizeOptionValue(variant),
                  ),
                ),
            );
          }
          return altHashtags.some((tag) =>
            tag
              .split("|")
              .some((variant) =>
                lowercaseOptions.includes(
                  this.normalizeOptionValue(variant),
                ),
              ),
          );
        });
      }

      slideMatchesAltFilterMode(
        altRaw,
        lowercaseOptionsNonEmpty,
        hashtagMatchAll,
      ) {
        const alt = altRaw ?? "";
        const collapsed = alt.replace(/\s+/g, " ").trim();
        if (collapsed.length === 0) return true;

        const hashtagMatches = alt.match(/#[^\s#]+/g) || [];
        /*
          Reserved flags (VARIANT_GALLERY_LEAD_TOKEN +
          VARIANT_GALLERY_COLLECTION_LEAD_TOKEN) ride on the same hashtag
          syntax merchants already know, but must not participate in
          filter membership — they're metadata about how to *display* (or
          not display) the slide, not about whether to show it. Without
          this exemption, an alt like "Hero #blue #lead" would have
          hashtagGroups = ["blue", "lead"], and in hashtagMatchAll mode
          the matcher would require BOTH to match a variant option;
          "lead" never matches anything, so the slide gets filtered out
          and the merchant's lead never appears. Keep the raw list (so
          `findLeadMediaIdForOptions` can still ask "does this slide
          carry #lead?") but pass the filtered list into the filter math.
        */
        const hashtagGroupsRaw = hashtagMatches.map((h) =>
          h.slice(1).toLowerCase(),
        );
        const hashtagGroups = hashtagGroupsRaw.filter(
          (tag) =>
            tag !== VARIANT_GALLERY_LEAD_TOKEN &&
            tag !== VARIANT_GALLERY_COLLECTION_LEAD_TOKEN,
        );

        let plainRemainder = alt;
        for (const h of hashtagMatches) {
          plainRemainder = plainRemainder.split(h).join(" ");
        }
        plainRemainder = plainRemainder.replace(/\s+/g, " ").trim();

        const plainTokens = plainRemainder
          .split(/[\s,]+/)
          .map((t) => this.normalizeOptionValue(t))
          .filter((t) => t.length > 0);

        const hasHashtagGroups = hashtagGroups.length > 0;
        const hasPlainTokens = plainTokens.length > 0;

        if (!hasHashtagGroups && !hasPlainTokens) return true;

        if (
          plainTokens.includes("all") ||
          hashtagGroups.some((tag) => tag.split("|").includes("all"))
        ) {
          return true;
        }

        const noSelectedOptions = lowercaseOptionsNonEmpty.length === 0;
        if (noSelectedOptions) return true;

        const matchHashtags = (() => {
          if (!hasHashtagGroups) return true;
          if (hashtagMatchAll) {
            return hashtagGroups.every((tag) =>
              tag
                .split("|")
                .some((variant) =>
                  lowercaseOptionsNonEmpty.includes(
                    this.normalizeOptionValue(variant),
                  ),
                ),
            );
          }
          return hashtagGroups.some((tag) =>
            tag
              .split("|")
              .some((variant) =>
                lowercaseOptionsNonEmpty.includes(
                  this.normalizeOptionValue(variant),
                ),
              ),
          );
        })();

        const matchPlain = (() => {
          if (!hasPlainTokens) return true;
          const plainNorm = this.normalizeOptionValue(plainRemainder);
          if (
            plainNorm.length > 0 &&
            lowercaseOptionsNonEmpty.includes(plainNorm)
          ) {
            return true;
          }
          const anyToken = !this.plainTokenMatchModeIsAll();
          if (anyToken) {
            return plainTokens.some((token) =>
              lowercaseOptionsNonEmpty.includes(token),
            );
          }
          return plainTokens.every((token) =>
            lowercaseOptionsNonEmpty.includes(token),
          );
        })();

        return matchHashtags && matchPlain;
      }

      filterSlidesByOptions(
        slides,
        options,
        featured_media_id,
        matchAll = true,
      ) {
        if (!this.isVariantAltFilterEnabled()) {
          return this.filterSlidesByOptionsLegacy(
            slides,
            options,
            featured_media_id,
            matchAll,
          );
        }

        const lowercaseOptionsNonEmpty = options
          .map((option) => this.normalizeOptionValue(option))
          .filter((o) => o.length > 0);

        return slides.filter((slide) => {
          const media = getSlideFilterMedia(slide);
          const alt = media ? media.getAttribute("alt") : "";
          const mediaId = media ? media.getAttribute("data-media-id") : "";

          if (
            featured_media_id &&
            String(mediaId) === String(featured_media_id)
          ) {
            return true;
          }

          return this.slideMatchesAltFilterMode(
            alt,
            lowercaseOptionsNonEmpty,
            matchAll,
          );
        });
      }

      galleryUpdateEvent(opt) {
        const galleryEvent = new CustomEvent("gallery:updated", {
          bubbles: true,
          cancelable: true,
          detail: {
            desc: "gallery updated",
            selector: ".wt-product__gallery",
            ...opt,
          },
        });
        document.dispatchEvent(galleryEvent);
      }

      filterSlides(
        options,
        featured_media_id,
        matchAll = true,
        callback,
        depth = 0,
      ) {
        if (!this.elements) return;
        const section = this.closest("section[data-product-handle]");
        const optionsForMatch = this.resolveOptionsForAltMatch(
          options,
          section,
        );

        const originalGallerySlides = Array.from(
          this._masterGallerySlides || this.elements.gallerySlides || [],
          (el) => el.cloneNode(true),
        );
        const originalThumbsSlides = Array.from(
          this._masterThumbsSlides || this.elements.thumbsSlides || [],
          (el) => el.cloneNode(true),
        );

        const getMediaId = (item) => item?.dataset?.mediaId;

        let filteredGallerySlides = this.filterSlidesByOptions(
          originalGallerySlides,
          optionsForMatch,
          featured_media_id,
          matchAll,
        );
        let filteredThumbsSlides = this.filterSlidesByOptions(
          originalThumbsSlides,
          optionsForMatch,
          featured_media_id,
          matchAll,
        );

        /*
          Same empty-fallback guard as applyInitialAltFilterBeforeInit:
          if the filter dropped everything for the freshly-selected
          variant we restore the unfiltered master set but skip the
          lead hoist so we don't elevate a slide tagged for a
          different variant.
        */
        const filterReturnedEmpty = filteredGallerySlides.length === 0;
        if (filterReturnedEmpty) {
          filteredGallerySlides = [...originalGallerySlides];
          filteredThumbsSlides = [...originalThumbsSlides];
        }

        /*
          Pre-sort: locate the variant's lead media id (if any) so
          sortSlides can place it at index 0 and the variant featured
          at index 1. See findLeadMediaIdAmongFiltered for the scoping
          semantics — short version: anything that survived the
          existing filter is already in-scope, so we just look for the
          first surviving `#lead`.
        */
        const leadMediaId = filterReturnedEmpty
          ? null
          : this.findLeadMediaIdAmongFiltered(filteredGallerySlides);

        if (featured_media_id || leadMediaId) {
          filteredGallerySlides = this.sortSlides(
            filteredGallerySlides,
            featured_media_id,
            leadMediaId,
          );
          filteredThumbsSlides = this.sortSlides(
            filteredThumbsSlides,
            featured_media_id,
            leadMediaId,
          );
        }

        const renderedSlidesChanged =
          this.renderedSlides.map(getMediaId).toString() !==
          filteredGallerySlides.map(getMediaId).toString();

        if (this.gallerySwiper && renderedSlidesChanged) {
          if (this._isInQuickBuyDrawer()) {
            this.beforeGalleryChange();
            const gallerySlidesWrapper = this.querySelector(
              "[data-gallery] [data-swiper-container]",
            );
            this.gallerySwiper.removeAllSlides();
            gallerySlidesWrapper.innerHTML = "";
            const swiperContainer = this.querySelector(
              "[data-gallery] .swiper-wrapper",
            );
            filteredGallerySlides.forEach((slide, idx) => {
              if (idx === 0) {
                const slideImg = slide.querySelector("img");
                if (slideImg)
                  slideImg.onload = function () {
                    swiperContainer.style.height = "auto";
                  };
              }
              this.gallerySwiper.appendSlide(slide);
            });
            this.decorateSwiper(this.elements.gallery);
            this.gallerySwiper.update();
            handleGallerySlideVideos(this.gallerySwiper);
          } else if (this.thumbsSwiper) {
            this.beforeGalleryChange();
            const thumbSlidesWrapper = this.querySelector(
              "[data-thumbs] [data-swiper-container]",
            );
            const gallerySlidesWrapper = this.querySelector(
              "[data-gallery] [data-swiper-container]",
            );

            this.gallerySwiper.removeAllSlides();
            gallerySlidesWrapper.innerHTML = "";
            const swiperContainer = this.querySelector(".swiper-wrapper");
            filteredGallerySlides.forEach((slide, idx) => {
              if (idx === 0) {
                const slideImg = slide.querySelector("img");
                if (slideImg)
                  slideImg.onload = function () {
                    swiperContainer.style.height = "auto";
                  };
              }
              this.gallerySwiper.appendSlide(slide);
            });
            this.decorateSwiper(this.elements.gallery);

            this.thumbsSwiper.removeAllSlides();
            thumbSlidesWrapper.innerHTML = "";

            filteredThumbsSlides.forEach((slide) =>
              this.thumbsSwiper.appendSlide(slide),
            );

            this.decorateSwiper(
              this.elements.thumbs,
              "wt-slider__container--thumbs",
            );

            this.thumbsSwiper.update();
            this.gallerySwiper.update();
            handleGallerySlideVideos(this.gallerySwiper);
          }
        } else if (
          !this._isInQuickBuyDrawer() &&
          (this.configuration.desktopLayout == "collage" ||
            this.configuration.desktopLayout == "masonry" ||
            this.configuration.desktopLayout == "fashion")
        ) {
          if (renderedSlidesChanged) {
            this.beforeGalleryChange();
            // Remove all existing slides
            this.elements.gallery
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());
            this.elements.thumbs
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());

            filteredGallerySlides.forEach((slide) =>
              this.elements.gallery
                .querySelector("[data-swiper-container]")
                .append(slide),
            );
            filteredThumbsSlides.forEach((slide) =>
              this.elements.thumbs
                .querySelector("[data-swiper-container]")
                .append(slide),
            );

            const layoutForDecorate =
              this.configuration.desktopLayout == "fashion"
                ? "masonry"
                : this.configuration.desktopLayout;
            this.decorateCollage(
              this.elements.gallery,
              layoutForDecorate,
            );

            // add wt-product__gallery --even --odd classes to MediaGallery container depends on number of slides
            this.elements.gallery.classList.remove("wt-product__gallery--even");
            this.elements.gallery.classList.remove("wt-product__gallery--odd");
            if (filteredGallerySlides.length % 2 == 0) {
              this.elements.gallery.classList.add("wt-product__gallery--even");
            } else {
              this.elements.gallery.classList.add("wt-product__gallery--odd");
            }
            initStaticGalleryProductVideos(this.elements.gallery);
          }
        }

        /*
          When the merchant has designated a #lead for the active
          variant, that slide is what should be visible on the PDP
          (and that's what we just hoisted to position 0). The variant
          featured_media is still in the gallery — moved to position 1
          by sortSlides — and remains the image used everywhere else
          (cart, checkout, collections via card_img). Honor that intent
          by activating the lead's media id, not the featured's, so
          Swiper actually slides to it instead of jumping to the
          adjacent featured slot.
        */
        const activeMediaId = leadMediaId || featured_media_id;
        if (activeMediaId) {
          this.setActiveMedia(activeMediaId, true);
        } else {
          this.setActiveMedia(
            getSlideFilterMedia(filteredGallerySlides[0])?.getAttribute(
              "data-media-id",
            ),
            true,
          );
        }
        this.gallerySwiper?.update();

        if (
          renderedSlidesChanged &&
          (!this._isInQuickBuyDrawer() || this.gallerySwiper)
        ) {
          setTimeout(this.afterGalleryChange.bind(this), 300);
        }

        // Quick buy: the first pass can run before Swiper exists, so the block
        // above never updates the DOM. If we still assign renderedSlides here,
        // the next updateGallery() sees "no change" and never appends slides.
        const quickBuySwiperNotReady =
          this._isInQuickBuyDrawer() &&
          !this.gallerySwiper &&
          renderedSlidesChanged;
        if (!quickBuySwiperNotReady) {
          this.renderedSlides = [...filteredGallerySlides];
        }

        if (quickBuySwiperNotReady && depth === 0) {
          queueMicrotask(() => {
            this.filterSlides(
              options,
              featured_media_id,
              matchAll,
              callback,
              depth + 1,
            );
          });
        }
      }

      setActiveMedia(mediaId, prepend) {
        let media = this.elements.gallery.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.gallerySwiper != null) {
          this.gallerySwiper.slideTo(this.indexInParent(media));
          this.thumbsSwiper?.slideTo(this.indexInParent(media));
        }
      }

      matchResolution() {
        /* Quick view drawer is max ~50rem; window may still be “desktop” width, which
         selected collage/masonry and showed every slide stacked with no Swiper. */
        if (
          this.closest(".wt__quick-buy") ||
          this.closest(".wt__quick-buy--wrapper") ||
          this.closest(".wt__quick-buy__container")
        ) {
          return "mobile";
        }
        if (window.innerWidth < this.configuration.sliderEnabledBreakpoint) {
          return "mobile";
        } else {
          return "desktop";
        }
      }

      indexInParent(node) {
        if (!node) return -1;
        let children = node.parentNode.childNodes;
        let num = 0;
        for (let i = 0; i < children.length; i++) {
          if (children[i] == node) return num;
          if (children[i].nodeType == 1) num++;
        }
        return -1;
      }
    },
  );
}
